import { useState, useEffect } from 'react';
import { api } from '../../../services/api';
import type { CodingProject } from './types';
import { 
  Button, 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogFooter, 
  DialogTitle, 
  DialogDescription,
  Input,
  Label,
  Textarea,
  RadixSelect,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui';
import { logger } from '../../../utils/logger';

interface CodingTabProps {
  projectId: string;
  onOpenCodingProject?: (codingProject: {
    codingProjectId: string;
    projectId: string;
    title: string;
    projectType: CodingProject['projectType'];
    status: CodingProject['status'];
  }) => void;
  codingProjects: CodingProject[];
  setCodingProjects: (projects: CodingProject[]) => void;
}

export function CodingTab({ projectId, onOpenCodingProject, codingProjects, setCodingProjects }: CodingTabProps) {
  const [loadingCodingProjects, setLoadingCodingProjects] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  
  const [showNewCodingModal, setShowNewCodingModal] = useState(false);
  const [showDeleteCodingModal, setShowDeleteCodingModal] = useState(false);
  const [codingToDelete, setCodingToDelete] = useState<CodingProject | null>(null);
  const [newCodingTitle, setNewCodingTitle] = useState('');
  const [newCodingType, setNewCodingType] = useState<CodingProject['projectType']>('react-typescript');
  const [newCodingDescription, setNewCodingDescription] = useState('');
  const [isCreatingCoding, setIsCreatingCoding] = useState(false);
  const [isDeletingCoding, setIsDeletingCoding] = useState(false);

  useEffect(() => {
    if (hasLoaded) return;
    const load = async () => {
      setLoadingCodingProjects(true);
      try {
        const response = await api.get(`/projects/${projectId}/coding-projects`);
        setCodingProjects(response.data.data || []);
        setHasLoaded(true);
      } catch (err) {
        logger.error('Failed to load coding projects:', err);
        setCodingProjects([]);
      } finally {
        setLoadingCodingProjects(false);
      }
    };
    load();
  }, [projectId, hasLoaded, setCodingProjects]);

  const handleCodingProjectClick = (codingProject: CodingProject) => {
    if (onOpenCodingProject) {
      onOpenCodingProject({
        codingProjectId: codingProject.codingProjectId,
        projectId,
        title: codingProject.title,
        projectType: codingProject.projectType,
        status: codingProject.status,
      });
    }
  };

  const handleCreateCodingProject = async () => {
    if (!newCodingTitle.trim()) return;
    
    setIsCreatingCoding(true);
    try {
      const response = await api.post(`/projects/${projectId}/coding-projects`, {
        title: newCodingTitle.trim(),
        projectType: newCodingType,
        description: newCodingDescription.trim() || undefined,
      });
      const newProject = response.data.data as CodingProject;
      setCodingProjects([...codingProjects, newProject]);
      setShowNewCodingModal(false);
      setNewCodingTitle('');
      setNewCodingType('react-typescript');
      setNewCodingDescription('');
      
      if (onOpenCodingProject) {
        onOpenCodingProject({
          codingProjectId: newProject.codingProjectId,
          projectId,
          title: newProject.title,
          projectType: newProject.projectType,
          status: newProject.status,
        });
      }
    } catch (err) {
      logger.error('Failed to create coding project:', err);
    } finally {
      setIsCreatingCoding(false);
    }
  };

  const handleDeleteCodingProject = async () => {
    if (!codingToDelete) return;
    
    setIsDeletingCoding(true);
    try {
      await api.delete(`/projects/${projectId}/coding-projects/${codingToDelete.codingProjectId}`);
      setCodingProjects(codingProjects.filter(c => c.codingProjectId !== codingToDelete.codingProjectId));
      setShowDeleteCodingModal(false);
      setCodingToDelete(null);
    } catch (err) {
      logger.error('Failed to delete coding project:', err);
    } finally {
      setIsDeletingCoding(false);
    }
  };

  const openDeleteCodingModal = (codingProject: CodingProject, e: React.MouseEvent) => {
    e.stopPropagation();
    setCodingToDelete(codingProject);
    setShowDeleteCodingModal(true);
  };

  return (
    <div className="space-y-4">
      {/* Header with New Coding Project button */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          AI-powered coding projects with autonomous development
        </p>
        <Button
          onClick={() => setShowNewCodingModal(true)}
          variant="outline"
          size="sm"
          className="gap-1"
        >
          <span>➕</span> New Coding Project
        </Button>
      </div>
      
      {/* Coding projects list */}
      {loadingCodingProjects ? (
        <div className="text-center py-8">
          <div className="animate-spin text-2xl">⏳</div>
        </div>
      ) : codingProjects.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <div className="text-4xl mb-3">💻</div>
          <p className="mb-2">No coding projects yet</p>
          <p className="text-xs">Create a coding project to start AI-assisted development</p>
          <Button
            onClick={() => setShowNewCodingModal(true)}
            variant="outline"
            size="sm"
            className="mt-4"
          >
            Create First Coding Project
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {codingProjects.map(codingProject => (
            <div
              key={codingProject.codingProjectId}
              className="p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
              onClick={() => handleCodingProjectClick(codingProject)}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">💻</span>
                    <span className="text-sm font-medium text-foreground">
                      {codingProject.title}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {codingProject.projectType}
                  </p>
                  {codingProject.description && (
                    <p className="text-xs text-muted-foreground mt-1 truncate">
                      {codingProject.description}
                    </p>
                  )}
                  {codingProject.previewUrl && (
                    <a
                      href={codingProject.previewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary hover:underline mt-1 inline-block"
                      onClick={(e) => e.stopPropagation()}
                    >
                      🔗 {codingProject.previewUrl}
                    </a>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={(e) => openDeleteCodingModal(codingProject, e)}
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    title="Delete"
                  >
                    <span className="text-sm">🗑️</span>
                  </Button>
                  <div className="flex flex-col items-end gap-1">
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      codingProject.status === 'deployed' ? 'bg-success-light text-success' :
                      codingProject.status === 'preview' ? 'bg-info-light text-info' :
                      codingProject.status === 'building' ? 'bg-warning-light text-warning' :
                      codingProject.status === 'developing' ? 'bg-primary/10 text-primary' :
                      codingProject.status === 'error' ? 'bg-destructive/10 text-destructive' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      {codingProject.status}
                    </span>
                    {codingProject.lastBuildStatus && (
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        codingProject.lastBuildStatus === 'success' ? 'bg-success-light text-success' :
                        codingProject.lastBuildStatus === 'failed' ? 'bg-destructive/10 text-destructive' :
                        'bg-warning-light text-warning'
                      }`}>
                        Build: {codingProject.lastBuildStatus}
                      </span>
                    )}
                    {codingProject.lastTestStatus && (
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        codingProject.lastTestStatus === 'passed' ? 'bg-success-light text-success' :
                        codingProject.lastTestStatus === 'failed' ? 'bg-destructive/10 text-destructive' :
                        'bg-warning-light text-warning'
                      }`}>
                        Tests: {codingProject.lastTestStatus}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* New Coding Project Modal */}
      <Dialog open={showNewCodingModal} onOpenChange={setShowNewCodingModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>New Coding Project</DialogTitle>
            <DialogDescription>
              Create a new AI-powered coding project. The AI will help you develop, test, and deploy code.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="coding-title">Project Name</Label>
              <Input
                id="coding-title"
                value={newCodingTitle}
                onChange={(e) => setNewCodingTitle(e.target.value)}
                placeholder="e.g., User Dashboard"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="coding-type">Project Type</Label>
              <RadixSelect value={newCodingType} onValueChange={(v) => setNewCodingType(v as CodingProject['projectType'])}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select project type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="react-typescript">React TypeScript</SelectItem>
                  <SelectItem value="node-api">Node.js API</SelectItem>
                  <SelectItem value="full-stack">Full Stack</SelectItem>
                  <SelectItem value="python">Python</SelectItem>
                  <SelectItem value="c-native">C/Native</SelectItem>
                </SelectContent>
              </RadixSelect>
            </div>
            <div>
              <Label htmlFor="coding-description">Description (optional)</Label>
              <Textarea
                id="coding-description"
                value={newCodingDescription}
                onChange={(e) => setNewCodingDescription(e.target.value)}
                placeholder="Brief description of what this project will do..."
                className="mt-1"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setShowNewCodingModal(false)}
              variant="outline"
              disabled={isCreatingCoding}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateCodingProject}
              disabled={isCreatingCoding || !newCodingTitle.trim()}
            >
              {isCreatingCoding ? 'Creating...' : 'Create Project'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Coding Project Modal */}
      <Dialog open={showDeleteCodingModal && !!codingToDelete} onOpenChange={(open) => {
        if (!open) {
          setShowDeleteCodingModal(false);
          setCodingToDelete(null);
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete Coding Project</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this coding project? This will remove all code, builds, and preview deployments. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {codingToDelete && (
            <div className="py-4">
              <div className="bg-muted/30 rounded-lg p-3 text-sm space-y-2">
                <div>
                  <strong>Project:</strong> {codingToDelete.title}
                </div>
                <div>
                  <strong>Type:</strong> {codingToDelete.projectType}
                </div>
                <div>
                  <strong>Status:</strong> {codingToDelete.status}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              onClick={() => {
                setShowDeleteCodingModal(false);
                setCodingToDelete(null);
              }}
              variant="outline"
              disabled={isDeletingCoding}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteCodingProject}
              disabled={isDeletingCoding}
              variant="destructive"
            >
              {isDeletingCoding ? 'Deleting...' : 'Delete Project'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
